import {
	AfterViewInit,
	Component,
	Input,
	OnChanges,
	Output,
	SimpleChanges,
	ElementRef,
	EventEmitter,
	ViewChild,
	OnDestroy
} from '@angular/core';
import { StateService } from '@uirouter/angular';
import { NgForm, NgModel, ValidationErrors } from '@angular/forms';
import { distinctUntilChanged } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { ICategorySettings } from 'rev-shared/ui/categorySettings/ICategorySettings';
import { IMediaFeatures } from 'rev-shared/media/IMediaFeatures';
import { RecordingPolicy } from 'rev-shared/media/RecordingPolicy';
import { WebcastTemplatesService } from 'rev-shared/webcast/WebcastTemplates.Service';
import { EditWebcastRecordingSettingsComponent } from './EditWebcastRecordingSettingsComponent';


import {
	AutomatedDatePastError,
	InsufficientRevIQCredits,
	LIVE_SUBTITLES_ERRORS,
	MicrosoftTeamsTenantIdRequired,
	RtmpListenerUrlConflict,
	SecondaryRtmpListenerUrlConflict,
	ShortcutNotAvailableError,
	UploadFailedError,
	VideoSourceNotAvailableError
} from './EditWebcast.Service';

import { EditWebcastModel } from './EditWebcastModel';
import { PresentationFileStatus } from '../presentations/PresentationFileStatus';
import { ShortcutConflictErrorKey } from './EditWebcastBasicSettings.Component';
import { WebcastStatus } from '../webcast/WebcastStatus';
import { WebcastVideoSource } from '../webcast/WebcastVideoSource';

import styles from './EditWebcast.module.less';

@Component({
	selector: 'edit-webcast-form',
	templateUrl: './EditWebcastForm.Component.html',
	standalone: false
})
export class EditWebcastFormComponent implements OnChanges, AfterViewInit, OnDestroy {
	@Input() public webcast: EditWebcastModel;
	@Input() public features: IMediaFeatures;
	@Input() public isSidebar: boolean;
	@Input() public aiCreditsValid: boolean;
	@Input() public categories: ICategorySettings[];

	@Output() public onSaveTemplate: EventEmitter<any> = new EventEmitter();
	@Output() public onSubmit: EventEmitter<any> = new EventEmitter();
	@Output() public onCancel: EventEmitter<any> = new EventEmitter();
	@Output() public onTemplateSelected: EventEmitter<string> = new EventEmitter();
	@Output() public sourceRestartInitiated: EventEmitter<string> = new EventEmitter();
	@Output() public switchStreamInitiated: EventEmitter<void> = new EventEmitter();

	@ViewChild('videoSettings') public videoSettingsModel: NgModel;
	@ViewChild(EditWebcastRecordingSettingsComponent) public recordingSettings: EditWebcastRecordingSettingsComponent;

	@ViewChild('form')
	public form: NgForm;

	private readonly $element: HTMLElement;
	private readonly subscription: Subscription = new Subscription();

	public readonly styles = styles;
	public validationErrors: ValidationErrors;
	public basicSettingsErrors: ValidationErrors;
	public readonly RecordingPolicy = RecordingPolicy;
	public readonly WebcastVideoSource = WebcastVideoSource;

	constructor(
		elementRef: ElementRef,
		private readonly $state: StateService,
		public readonly WebcastTemplatesService: WebcastTemplatesService
	) {
		this.$element = elementRef.nativeElement;
	}

	public ngOnInit(): void {
		this.$element.classList.add(styles.editWebcastForm);

		if (this.isSidebar) {
			this.$element.classList.add(styles.editWebcastFormSidebar);
		}
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if(changes.webcast){
			this.mapWebcastCategories();
		}

		if (changes.aiCreditsValid) {
			this.validationErrors = this.aiCreditsValid ? undefined : { [InsufficientRevIQCredits] : true };
		}
	}

	public ngAfterViewInit(): void {
		window.setTimeout(() => {
			this.subscription.add(
				this.videoSettingsModel?.statusChanges
					.pipe(
						distinctUntilChanged()
					)
					.subscribe(val => this.updateBasicSettingsErrors())
			);
		}, 200);
	}

	public ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	public mapWebcastCategories(): void {
		this.webcast.categories = (this.webcast.categories || [])
			.map(cat => {
				const correlatedCategory = this.categories.find(item => item.categoryId === cat.categoryId);
				return {
					...correlatedCategory
				};
			});
	}

	public submit(): void {
		setTimeout(() => {
			this.onSubmit.emit({ failureCallback: (err: string) => this.handleError(err) });
		}, 100);
	}

	public cancel(): void {
		this.onCancel.emit();
	}

	public allowTemplate(): boolean {
		return !this.isSidebar && this.webcast.status !== WebcastStatus.Completed;
	}

	public get showRecordingSettings(): boolean {
		return this.features.recordingPolicy !== RecordingPolicy.DISABLE
			&& this.webcast.videoSource !== WebcastVideoSource.VOD;
	}

	public selectTemplate(templateId: string): void {
		this.onTemplateSelected.emit(templateId);
	}

	public onVideoSourceChanged(): void {
		this.recordingSettings.validateVideoBumpers();
	}

	private handleError(err: string): void {
		console.error('webcast save error', err);
		if (err === UploadFailedError) {
			this.webcast.presentation.status = PresentationFileStatus.UploadingFailed;
		} else if (err === AutomatedDatePastError) {
			this.validationErrors = { automatedDateInPast: true };
		} else if(err === VideoSourceNotAvailableError) {
			this.validationErrors = { presentationProfileConflict: true };
		} else if(err === ShortcutNotAvailableError) {
			this.validationErrors = { [ShortcutConflictErrorKey]: true };
		} else if(LIVE_SUBTITLES_ERRORS.includes(err) || err === RtmpListenerUrlConflict || err === SecondaryRtmpListenerUrlConflict) {
			this.validationErrors = { [err]: true };
		} else if (err === MicrosoftTeamsTenantIdRequired) {
			this.validationErrors = { [MicrosoftTeamsTenantIdRequired]: true };
		}

		this.updateBasicSettingsErrors();
	}

	public updateBasicSettingsErrors(): void {
		this.basicSettingsErrors = { ...this.validationErrors, ...this.videoSettingsModel?.errors };
	}
}
